import React, { forwardRef } from 'react';
import styles from './Badge.module.css';

/**
 * Badge — a Radix-Themes-style badge in Athena's idiom (TSX + CSS Module +
 * 2.0 tokens). `variant` and `color` are ORTHOGONAL axes:
 *
 *   - `variant`  — HOW it renders: solid | soft | surface | outline
 *   - `color`    — WHICH palette colour (accent = theme --primary)
 *   - `size`     — 1 | 2 | 3
 *   - `radius`   — none | small | medium | large | full
 *   - `highContrast` — stronger label / deepened solid
 *
 * `preset` is a convenience axis that pre-fills the above to reproduce the
 * earlier shadcn-style looks (default / secondary / destructive / outline /
 * ghost / link). Explicit props override the preset.
 *
 * Distinct from the domain pills (StatusPill / CategoryPill / LevelBadge),
 * which keep their own semantics.
 */

export type BadgeSize = '1' | '2' | '3';
export type BadgeVariant = 'solid' | 'soft' | 'surface' | 'outline';
export type BadgeRadius = 'none' | 'small' | 'medium' | 'large' | 'full';
export type BadgeColor =
  | 'accent'
  | 'gray'
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'green'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'pink';
export type BadgePreset =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'ghost'
  | 'link';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: BadgeSize;
  variant?: BadgeVariant;
  color?: BadgeColor;
  radius?: BadgeRadius;
  highContrast?: boolean;
  /** Pre-fills variant/color/radius to reproduce an earlier shadcn-style
   *  look. Explicit props win over the preset's values. */
  preset?: BadgePreset;
}

const SIZE_CLASS: Record<BadgeSize, string> = {
  '1': styles.s1,
  '2': styles.s2,
  '3': styles.s3,
};

const VARIANT_CLASS: Record<BadgeVariant, string> = {
  solid: styles.vSolid,
  soft: styles.vSoft,
  surface: styles.vSurface,
  outline: styles.vOutline,
};

const RADIUS_CLASS: Record<BadgeRadius, string> = {
  none: styles.rNone,
  small: styles.rSmall,
  medium: styles.rMedium,
  large: styles.rLarge,
  full: styles.rFull,
};

const COLOR_CLASS: Record<BadgeColor, string> = {
  accent: styles.cAccent,
  gray: styles.cGray,
  red: styles.cRed,
  orange: styles.cOrange,
  amber: styles.cAmber,
  yellow: styles.cYellow,
  green: styles.cGreen,
  teal: styles.cTeal,
  cyan: styles.cCyan,
  sky: styles.cSky,
  blue: styles.cBlue,
  indigo: styles.cIndigo,
  purple: styles.cPurple,
  pink: styles.cPink,
};

// Presets that resolve to the orthogonal axes.
const PRESET_PROPS: Record<
  'default' | 'secondary' | 'destructive' | 'outline',
  { variant: BadgeVariant; color: BadgeColor; radius: BadgeRadius }
> = {
  default: { variant: 'solid', color: 'accent', radius: 'medium' },
  secondary: { variant: 'soft', color: 'gray', radius: 'medium' },
  destructive: { variant: 'solid', color: 'red', radius: 'medium' },
  outline: { variant: 'outline', color: 'gray', radius: 'medium' },
};

// ghost / link don't map to a variant×color combo — they get a dedicated
// self-contained class.
const PRESET_STANDALONE: Record<'ghost' | 'link', string> = {
  ghost: styles.pGhost,
  link: styles.pLink,
};

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    { size = '1', variant, color, radius, highContrast = false, preset, className = '', children, ...props },
    ref,
  ) => {
    const standalone = preset === 'ghost' || preset === 'link' ? preset : null;
    const presetProps =
      preset && !standalone ? PRESET_PROPS[preset as keyof typeof PRESET_PROPS] : null;

    const sizeCls = SIZE_CLASS[size] ?? SIZE_CLASS['1'];
    const radiusCls = RADIUS_CLASS[radius ?? presetProps?.radius ?? 'medium'];

    let toneCls: string;
    if (standalone) {
      toneCls = PRESET_STANDALONE[standalone];
    } else {
      const effVariant = variant ?? presetProps?.variant ?? 'soft';
      const effColor = color ?? presetProps?.color ?? 'accent';
      toneCls = `${VARIANT_CLASS[effVariant]} ${COLOR_CLASS[effColor]}${highContrast ? ` ${styles.hc}` : ''}`;
    }

    return (
      <span
        ref={ref}
        data-slot="badge"
        className={`${styles.badge} ${sizeCls} ${toneCls} ${radiusCls} ${className}`.trim()}
        {...props}
      >
        {children}
      </span>
    );
  },
);

Badge.displayName = 'Badge';

export default Badge;
export { Badge };
